/**
 * Helpers for WebSocket message payloads (reaction vs new message).
 */

/** Locked-bubble fields redacted on the server; must ride live WS/poll maps. */
export function pickSensitiveMessageFields(data = {}) {
  const sensitive = data.sensitive === true || data.sensitive === 'true';
  return {
    sensitive,
    sensitiveTitle: data.sensitiveTitle || '',
    allowReceiverPin: data.allowReceiverPin === true,
    hasSenderPin: data.hasSenderPin === true || data.hasSenderPin === 'true',
  };
}

/** Live Google Translate fields; WS/poll mappers must not drop these. */
export function pickChatTranslationFields(data = {}) {
  const fields = {};
  if (data.sourceLanguage) {
    fields.sourceLanguage = data.sourceLanguage;
  }
  if (data.translations && typeof data.translations === 'object') {
    fields.translations = data.translations;
  }
  if (data.displayContent != null) {
    fields.displayContent = data.displayContent;
  }
  if (Object.prototype.hasOwnProperty.call(data, 'translationPending') && data.translationPending != null) {
    fields.translationPending = data.translationPending === true || data.translationPending === 'true';
  }
  return fields;
}

export function isMessageContentUnchanged(existing, incoming) {
  if (!existing || !incoming) return false;
  return (
    existing.content === (incoming.content || '') &&
    existing.senderId === incoming.senderId &&
    Boolean(existing.isEdited) === Boolean(incoming.isEdited) &&
    JSON.stringify(existing.attachments || []) ===
      JSON.stringify(incoming.attachments || []) &&
    (existing.replyToMessageId || null) === (incoming.replyToMessageId || null)
  );
}

/** User ids on incoming reactions. */
export function reactionUserIdSet(message) {
  const ids = new Set();
  for (const reaction of message?.reactions || []) {
    for (const userId of reaction?.userIds || []) {
      if (userId != null && String(userId)) ids.add(String(userId));
    }
  }
  return ids;
}

/**
 * True when this broadcast is an echo of a reaction involving the current user,
 * including after an optimistic local update already applied that reaction.
 */
export function isOwnReactionBroadcast(existing, incoming, currentUserId) {
  if (!currentUserId || !incoming) return false;
  const mine = String(currentUserId);
  if (!reactionUserIdSet(incoming).has(mine)) return false;
  if (!existing) return true;
  if (isMessageContentUnchanged(existing, incoming)) return true;
  return !reactionUserIdSet(existing).has(mine);
}

/** True when only reactions and/or read/delivery receipts changed (not a new message). */
export function isReactionOrReadUpdate(existing, incoming) {
  if (!isMessageContentUnchanged(existing, incoming)) return false;
  const reactionsChanged =
    JSON.stringify(existing.reactions || []) !==
    JSON.stringify(incoming.reactions || []);
  const readByChanged =
    JSON.stringify(existing.readBy || []) !==
    JSON.stringify(incoming.readBy || []);
  const deliveredByChanged =
    JSON.stringify(existing.deliveredBy || []) !==
    JSON.stringify(incoming.deliveredBy || []);
  const deliveryFlagChanged =
    Boolean(existing.isDelivered) !== Boolean(incoming.isDelivered) ||
    Boolean(existing.isRead) !== Boolean(incoming.isRead);
  return reactionsChanged || readByChanged || deliveredByChanged || deliveryFlagChanged;
}

/** Skip new-message toasts for reaction broadcasts, including first-time echoes. */
export function shouldSkipMessageNotification(existing, incoming, currentUserId) {
  if (!incoming) return true;
  if (incoming.notificationInfo) return true;
  if (isOwnReactionBroadcast(existing, incoming, currentUserId)) return true;
  if (isReactionOrReadUpdate(existing, incoming)) return true;
  const reactions = incoming.reactions || [];
  if (!reactions.length) return false;
  const created = incoming.createdAt || incoming.timestamp;
  const updated = incoming.updatedAt;
  return Boolean(created && updated && String(created) !== String(updated));
}

export function isSendUsingApiTemplate(message) {
  return String(message?.templateInfo?.templateType || '').toUpperCase() === 'SEND_USING_API';
}

/** True when this payload is a live new message that should beep/speak/toast. */
export function shouldAnnounceIncomingMessage({
  existingMessage,
  incoming,
  currentUserId,
  currentChannelId,
  tabVisible = true,
  windowFocused = true,
}) {
  if (!incoming || !currentUserId) return false;
  const fromSelf = String(incoming.senderId) === String(currentUserId);
  if (fromSelf && !isSendUsingApiTemplate(incoming)) return false;
  if (shouldSkipMessageNotification(existingMessage, incoming, currentUserId)) return false;
  // Opening a chat (or a join replay) must not re-announce a message already on screen.
  if (existingMessage) return false;
  const viewingThisChat = currentChannelId != null
    && String(currentChannelId) === String(incoming.channelId);
  if (viewingThisChat && tabVisible && windowFocused) return false;
  return true;
}
